/* global MR, braintree */
import Vue from 'vue';
import vuePaymentSvc from "../../services/vuePaymentSvc";
import { isSameAddress } from "../../utilities/mrVueUtils";
import { updatePaymentProfileForSubscriptions } from "../../services/vueSubscriptionSvc";
import { mrCardValidator } from '../../utilities/customValidators';
const { required, minLength } = require("vuelidate/lib/validators");

function PaymentProfile() {
  this.cc_cvc = '';
  this.cc_exp_month = '';
  this.cc_exp_year = '';
  this.cc_number = '';
  this.is_default = true;
  this.same_as_shipping = true;
  this.use_payment_for_all_subscriptions = true;
  this.ccType = '',
  this.ccCvLength = '',
  this.ccSize = '',
  this.cvvRegex = '',
  this.ccLength = '',
  this.ccMask = '',
  this.cvcMask = '',
  this.address = {
    first_name: '',
    last_name: '',
    street_1: '',
    street_2: '',
    city: '',
    post_code: '',
    region: '',
    phone: ''
  };
}

const ccTypes = {
  visa: {
    regex: /^4/,
    cvc: 3,
    cvcMask: '###',
    size: 16,
    formSize: 19,
    mask: '#### #### #### ####'
  },
  mc: {
    regex: /^(5[1-5]|2[2-7])/,
    cvc: 3,
    cvcMask: '###',
    size: 16,
    formSize: 19,
    mask: '#### #### #### ####'
  },
  amex: {
    regex: /^3[47]/,
    cvc: 4,
    cvcMask: '####',
    size: 15,
    formSize: 18,
    mask: '#### ###### #####'
  },
  discover: {
    regex: /^6(?:011|5)/,
    cvc: 3,
    cvcMask: '###',
    size: 16,
    formSize: 19,
    mask: '#### #### #### ####'
  },
  default: {
    regex: /^[0-9]/,
    cvc: 3,
    cvcMask: '###',
    size: 16,
    formSize: 19,
    mask: '#### #### #### ####'
  }
};

const state = {
  profiles: [],
  selectedProfileId: null,
  editProfileForm: {},
  savingProfile: false,
  profileFormIsValid: false,
  lastSavedProfile: {},

  profileToDelete: {},
  profileFormValidators: {
    cc_number: {
      required,
      mrCardValidator: {}
    },

    cc_exp_month: {
      required
    },

    cc_exp_year: {
      required
    },

    cc_cvc: {
      required,
      minLength: minLength(3)
    }
  },
  validAmazonSession: false
};

export const getters = {
  getPaymentProfileById: (state) => (paymentProfileId) => {
    return state.profiles.find(profile => profile.paymentProfileId == paymentProfileId) || {};
  },

  profileIsEditable: () => (paymentGateway) => {
    return !['paypal', 'amazon'].includes(paymentGateway);
  },

  defaultPaymentProfile(state) {
    return state.profiles.find(profile => profile.is_default);
  },

  expiringSoon(state) {
    return state.profiles.find(profile => profile.expiresIn > 0 && profile.expiresIn < 30);
  }
};

export const actions = {
  testCardNum({ state, dispatch }) {
    if (state.editProfileForm.paymentProfileId) {
      return;
    }

    var key;
    var cType;

    for (key in ccTypes) {
      if (ccTypes[key].regex.test(state.editProfileForm.cc_number)) {
        cType = key;
        dispatch('setCcFormSettings', cType);
        return;
      }
    }

    dispatch('setCcFormSettings', 'default');
  },

  setCcFormSettings({ commit, state }, cType) {
    if (state.editProfileForm.ccType == cType) {
      return;
    }

    commit('setCcType', cType);
    commit('setCcCvLength', ccTypes[cType].cvc);
    commit('setCcLength', ccTypes[cType].formSize);
    commit('setCcMask', ccTypes[cType].mask);
    commit('setCvcMask', ccTypes[cType].cvcMask);
    commit('setCcSize', ccTypes[cType].size);
    commit('setCcCvLengthValidation', minLength(state.editProfileForm.ccCvLength));
    commit('setCcNumberValidator', mrCardValidator(state.editProfileForm.ccSize));
  },

  //- use this when need to ensure that payments are loaded
  initPaymentProfiles({ state, dispatch }) {
    if (state.profiles.length) {
      return;
    }
    dispatch('getCustomerPaymentProfiles');
  },
  //- payment refresh/load
  getCustomerPaymentProfiles({ commit, dispatch, state, getters }) {
    vuePaymentSvc.getPaymentProfiles()
      .then(res => {
        commit('setPaymentProfiles', res.data);
        //- ensure default payment profile
        if (state.profiles.length > 0 && !getters.defaultPaymentProfile) {
          dispatch('updateDefaultPaymentProfile', { paymentProfileId: state.profiles[0].paymentProfileId });
        }
      })
      .catch(() => {
        dispatch('notifyError', null, { root: true});
      });
  },

  populateEditProfileForm({ commit, dispatch, rootGetters }, paymentProfile) {
    paymentProfile = paymentProfile || new PaymentProfile();
    let defaultShipAddress = rootGetters['addressBook/defaultShipAddress'];
    if (paymentProfile.address) {
      paymentProfile.address.isBillingAddress = true;
    }

    if (defaultShipAddress && paymentProfile.paymentProfileId) {
      //- compare payment address to default ship address and set same_as_shipping accordingly
      paymentProfile.same_as_shipping = isSameAddress(paymentProfile.address, defaultShipAddress);

      if (paymentProfile.goodThru) {
        paymentProfile.cc_exp_month = Vue.moment(paymentProfile.goodThru).month() + 1;
        paymentProfile.cc_exp_year = Vue.moment(paymentProfile.goodThru).year();
      }

      paymentProfile.use_payment_for_all_subscriptions = false;
    }

    if (!defaultShipAddress) {
      paymentProfile.same_as_shipping = false;
    }

    dispatch('addressBook/populateEditAddressForm', paymentProfile.address, { root: true });
    commit('setEditProfileForm', paymentProfile);
  },

  validateProfileForm({ commit }) {
    //- by setting it to true it will trigger watch on
    //- addressFormInputs and call $touch()
    //- always start with invalid profile
    commit('setProfileFormIsValid', false);
    commit('setValidateProfileForm', true);
  },

  saveProfileForm({ commit, state, dispatch, rootGetters, rootState }) {
    if (state.savingProfile) {
      return;
    }

    commit('setSavingProfile', true);

    return new Promise((resolve, reject) => {
      if (state.editProfileForm.use_payment_for_all_subscriptions) {
        commit('updateProfileForSubs', rootGetters['subscriptions/activeSubscriptionIds']);
      }

      var payload = {
        paymentProfileId: state.editProfileForm.paymentProfileId,
        updateDeclinedCC: state.editProfileForm.isDeclined,
        is_default: state.editProfileForm.is_default,
        updateAllSubs: state.editProfileForm.updateAllSubs
      };

      var address = rootState.addressBook.editAddressForm;
      if (!address) {
        address = {};
      }

      if (state.editProfileForm.same_as_shipping) {
        let defaultShipAddress = rootGetters['addressBook/defaultShipAddress'];

        if (!defaultShipAddress.id) {
          dispatch('notifyError', 'Please complete your shipping information.', { root: true });
          commit('setSavingProfile', false);
          return reject();
        }

        address = Object.assign({}, defaultShipAddress);
      }

      var firstName = address.first_name;
      var lastName = address.last_name;

      var card = {
        cardholderName: [firstName, lastName].filter(Boolean).join(' '),
        expirationMonth: state.editProfileForm.cc_exp_month,
        expirationYear: state.editProfileForm.cc_exp_year,
        billingAddress: {
          firstName: firstName,
          lastName: lastName,
          streetAddress: address.street_1 || '',
          extendedAddress: address.street_2 || '',
          locality: address.city || '',
          region: address.region || '',
          postalCode: address.post_code || ''
        }
      };

      payload.address = address;

      if (state.editProfileForm.cc_number) {
        card.number = state.editProfileForm.cc_number;
      }

      if (state.editProfileForm.cc_cvc) {
        card.cvv = state.editProfileForm.cc_cvc;
      }

      vuePaymentSvc.getClientToken()
        .then(res => {
          var dataCollector = braintree.data.setup({
            kount: { environment: MR.config.braintreeEnvironment }
          });

          var client = new braintree.api.Client(res.data);
          client.tokenizeCard(card, (err, nonce) => {
            if (err) {
              dispatch('notifyError', null, { root: true });
              commit('setSavingProfile', false);
              return reject();
            }

            payload.nonce = nonce;
            payload.deviceData = dataCollector.deviceData;

            vuePaymentSvc.savePaymentProfile(payload)
              .then((res) => {
                commit('setSavingProfile', false);
                dispatch('notifySuccess', 'Thanks! Your billing information has been updated.', { root: true });
                //- because we allow user to save a new address while delete
                //- an address in one swoop lets check if there is one set for deletion
                if (state.profileToDelete.paymentProfileId) {
                  dispatch('deleteProfile');
                  return resolve();
                }

                commit('setSelectedProfileId', res.data.paymentProfileId);
                res.data.is_default = payload.is_default;
                dispatch('updateLastSavedProfile', res.data);
                resolve();
                dispatch('getCustomerPaymentProfiles');
                dispatch('clearEditProfileForm');
              })
              .catch(err => {
                let errMsg = null;
                let errRes = err.response;
                if ((errRes + '').toLowerCase().indexOf('credit card declined') >= 0) {
                  errMsg = 'Your card has been declined. Please verify your information and resubmit your order. Thank you!';
                } else if (errRes.data.message) {
                  errMsg = errRes.data.message;
                }

                dispatch('notifyError', errMsg, { root: true });
                commit('setSavingProfile', false);
                reject();
              });
          });
        })
        .catch(() => {
          dispatch('notifyError', null, { root: true });
          commit('setSavingProfile', false);
          reject();
        });
    });
  },

  updateDefaultPaymentProfile({ dispatch }, params) {
    vuePaymentSvc.setDefaultPaymentProfile({ paymentProfileId: params.paymentProfileId })
      .then(() => {
        dispatch('getCustomerPaymentProfiles');
        dispatch('notifySuccess', 'Your default payment method was successfully updated.', { root: true });
      })
      .catch(() => {
        dispatch('notifyError', null, { root: true });
      });
  },

  editPaymentModal({ dispatch }, paymentProfile) {
    //- add editable fields, can edit only exp month and year
    if (paymentProfile && paymentProfile.paymentProfileId) {
      paymentProfile.cc_exp_month = '';
      paymentProfile.cc_exp_year = '';
    }

    dispatch('populateEditProfileForm', paymentProfile);
    var modal = './PaymentProfiles/PaymentEditModal';
    dispatch('modal/showModal', { component: modal }, { root: true });
  },

  updateSubsModal({ commit, dispatch }) {
    dispatch('modal/showModal', { component: './PaymentProfiles/PaymentUpdateSubsModal' }, { root: true });
    commit('modal/setPersistent', true, { root: true });
  },

  deletePaymentModal({ commit, dispatch }, profile) {
    commit('setProfileToDelete', profile);
    var modal = './PaymentProfiles/PaymentDeleteModal';
    if (profile.subs.length) {
      dispatch('populateEditProfileForm');
      modal = './PaymentProfiles/PaymentDeleteHasSubsModal';
    }

    dispatch('modal/showModal', { component: modal }, { root: true });
  },

  deleteProfileWithSubReplace({ commit, dispatch, state }, paymentProfileId) {
    var subIds = state.profileToDelete.subs.map(sub => sub.id);
    //- if paymentProfileId passed use that for all subs
    if (paymentProfileId) {
      //- update all subs to new paymentProfileId and then delete the profile
      return dispatch('updatePaymentProfileForSubscriptions', { paymentProfileId, subIds })
        .then(() => {
          dispatch('deleteProfile');
        });
    }

    //- else check for new profileForm and save new payment, then use that for subsIds
    //- which gets handled in saveProfile action
    commit('updateProfileForSubs', subIds);
    dispatch('saveProfileForm');
  },

  deleteProfile({ commit, dispatch, state }) {
    let { paymentProfileId } = state.profileToDelete;
    if (!paymentProfileId) {
      return dispatch('notifyError', 'No payment method has been selected for deletion.', { root: true });
    }

    vuePaymentSvc.deletePaymentProfile({ paymentProfileId })
      .then(() => {
        commit('clearProfileToDelete');
        dispatch('getCustomerPaymentProfiles');
        dispatch('notifySuccess', 'Your payment method was successfully removed', { root: true });
      })
      .catch((err) => {
        var message = null;
        if (err.response && err.response.data && err.response.data.message) {
          message = err.response.data.message;
        }
        dispatch('notifyError', message, { root: true });
      });
  },

  updatePaymentProfileForSubscriptions({ dispatch }, params) {
    var payload = {
      subIds: params.subIds,
      paymentProfileId: params.paymentProfileId
    };

    return new Promise((resolve, reject) => {
      updatePaymentProfileForSubscriptions(payload)
        .then(() => {
          dispatch('subscriptions/loadCustomerSubs', null, { root: true });
          dispatch('notifySuccess', 'Payment method for all subscriptions has been updated', { root: true });
          resolve();
        })
        .catch(() => {
          dispatch('notifyError', null, { root: true });
          reject();
        });
    });
  },

  clearEditProfileForm({ commit, dispatch }) {
    commit('clearEditProfileForm');
    dispatch('addressBook/clearEditAddressForm', null, { root: true });
  },

  clearSelectedProfile({ commit }) {
    commit('setSelectedProfileId', null);
  },

  updateCardValidator({ commit }, ccSize) {
    let validator = mrCardValidator.call(ccSize);
    commit('setCardValidator', validator);
  },

  updateLastSavedProfile({ commit }, profile) {
    commit('setLastSavedProfile', profile);
  }

};

export const mutations = {
  setPaymentProfiles(state, val) {
    state.profiles = val;
  },

  setEditProfileForm(state, val) {
    state.editProfileForm = Object.assign({}, val);
  },

  setProfileToDelete(state, val) {
    state.profileToDelete = val;
  },

  setSavingProfile(state, val) {
    state.savingProfile = val;
  },

  setValidateProfileForm(state, val) {
    state.validateProfileForm = val;
  },

  setProfileFormIsValid(state, val) {
    state.profileFormIsValid = val;
  },

  setCardValidator(state, val) {
    state.profileFormValidators.cc_number.mrCardValidator = val;
  },

  setCcType(state, val) {
    state.editProfileForm.ccType = val;
  },

  setCcCvLength(state, val) {
    state.editProfileForm.ccCvLength = val;
  },

  setCcLength(state, val) {
    state.editProfileForm.ccLength = val;
  },

  setCcMask(state, val) {
    state.editProfileForm.ccMask = val;
  },

  setCvcMask(state, val) {
    state.editProfileForm.cvcMask = val;
  },

  setCcSize(state, val) {
    state.editProfileForm.ccSize = val;
  },

  setCcCvLengthValidation(state, val) {
    state.profileFormValidators.cc_cvc.minLength = val;
  },

  setCcNumberValidator(state, val) {
    state.profileFormValidators.cc_number.mrCardValidator = val;
  },

  setSelectedProfileId(state, val) {
    state.selectedProfileId = val;
  },

  setValidAmazonSession(state, val) {
    state.validAmazonSession = val;
  },

  setLastSavedProfile(state, val) {
    state.lastSavedProfile = val || {};
  },

  updateProfileForSubs(state, val) {
    state.editProfileForm.update_profile_for_subs = val;
  },

  updateUseProfileForAllSubs(state, val) {
    state.editProfileForm.use_payment_for_all_subscriptions = val;
  },

  updateCcNumber(state, val) {
    state.editProfileForm.cc_number = val;
  },

  updateExpMonth(state, val) {
    state.editProfileForm.cc_exp_month = val;
  },

  updateExpYear(state, val) {
    state.editProfileForm.cc_exp_year = val;
  },

  updateCvc(state, val) {
    state.editProfileForm.cc_cvc = val;
  },

  updateIsDefault(state, val) {
    state.editProfileForm.is_default = val;
  },

  updateSameAsShipping(state, val) {
    state.editProfileForm.same_as_shipping = val;
  },

  updateEditProfileFormIsValid(state, val) {
    state.editProfileForm.formIsValid = val;
  },

  clearEditProfileForm(state) {
    state.editProfileForm = {};
  },

  clearProfileToDelete(state) {
    state.profileToDelete = {};
  }

};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
